import {useCallback, useEffect, useState} from "react";
import type {FormEvent} from "react";
import {
  createWorkPackage,
  discardWorkPackage,
  listWorkPackageApprovers,
  listWorkPackages,
  listPrograms,
  listCapabilities,
  listWorkPackageVersions,
  readObjectContext,
  readWorkPackageRegistryActions,
  reviseWorkPackageApprover,
  submitWorkPackage,
  updateWorkPackage,
  validateWorkPackage,
  type ApproverIdentity,
  type AvailableAction,
  type Capability,
  type Program,
  type WorkPackage,
  type WorkPackageDraft,
  type WorkPackageValidation,
  type WorkPackageVersion
} from "../api/client";
import {useAuth} from "../auth/AuthContext";
import {PageHeader} from "../components/PageHeader";

const blankDraft: WorkPackageDraft = {
  key: "",
  title: "",
  objective: "",
  programId: null,
  capabilityId: null,
  ownerOrganization: "",
  supportingOrganizations: [],
  risk: "MEDIUM",
  priority: "MEDIUM",
  dependencies: [],
  deliverables: [],
  restrictions: [],
  acceptanceGates: [],
  namedApprover: "",
  evidenceRequirements: []
};

type Mode = "list" | "create" | "edit" | "preview" | "history" | "discard" | "correct-approver";

export function WorkPackageAuthoring() {
  const {authorizedFetch, user} = useAuth();
  const [items, setItems] = useState<WorkPackage[]>([]);
  const [selected, setSelected] = useState<WorkPackage | null>(null);
  const [draft, setDraft] = useState<WorkPackageDraft>(blankDraft);
  const [mode, setMode] = useState<Mode>("list");
  const [validation, setValidation] = useState<WorkPackageValidation | null>(null);
  const [versions, setVersions] = useState<WorkPackageVersion[]>([]);
  const [discardReason, setDiscardReason] = useState("");
  const [approvers, setApprovers] = useState<ApproverIdentity[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [capabilities, setCapabilities] = useState<Capability[]>([]);
  const [correctedApprover, setCorrectedApprover] = useState("");
  const [correctionReason, setCorrectionReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [registryActions, setRegistryActions] = useState<AvailableAction[]>([]);
  const [objectActions, setObjectActions] = useState<AvailableAction[]>([]);
  const mayAuthor = available(registryActions, "CREATE_WORK_PACKAGE");

  const refresh = useCallback(async (signal?: AbortSignal) => {
    const result = await listWorkPackages(authorizedFetch, signal);
    setItems(Array.isArray(result) ? result : []);
  }, [authorizedFetch]);

  useEffect(() => {
    const controller = new AbortController();
    void Promise.all([
      refresh(controller.signal),
      listPrograms(authorizedFetch, "", controller.signal)
        .then(result => setPrograms(Array.isArray(result)
          ? result.filter(program => program.lifecycle === "ACTIVE") : [])),
      listCapabilities(authorizedFetch, undefined, controller.signal)
        .then(result => setCapabilities(Array.isArray(result)
          ? result.filter(capability => capability.lifecycle === "AVAILABLE") : [])),
      listWorkPackageApprovers(authorizedFetch, controller.signal)
        .then(result => setApprovers(Array.isArray(result) ? result : [])),
      readWorkPackageRegistryActions(authorizedFetch, controller.signal)
        .then(result => setRegistryActions(Array.isArray(result) ? result : []))
    ]).catch(problem => {
      if (!controller.signal.aborted) setError(message(problem));
    });
    return () => controller.abort();
  }, [refresh, authorizedFetch]);

  const beginCreate = () => {
    setSelected(null);
    setDraft({...blankDraft, ownerOrganization: user?.company ?? ""});
    setValidation(null);
    setError("");
    setMode("create");
  };
  const beginEdit = (workPackage: WorkPackage) => {
    setSelected(workPackage);
    setDraft(toDraft(workPackage));
    setValidation(null);
    setError("");
    setMode("edit");
  };
  const preview = async (workPackage: WorkPackage) => {
    setSelected(workPackage);
    setValidation(null);
    setError("");
    setMode("preview");
    try {
      const context = await readObjectContext(
        authorizedFetch, "WORK_PACKAGE", workPackage.id
      );
      setObjectActions(context.availableActions);
    } catch (problem) {
      setObjectActions([]);
      setError(message(problem));
    }
  };
  const close = () => {
    setMode("list");
    setSelected(null);
    setValidation(null);
    setVersions([]);
    setObjectActions([]);
    setError("");
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    await run(async () => {
      const result = mode === "create"
        ? await createWorkPackage(authorizedFetch, draft)
        : await updateWorkPackage(authorizedFetch, required(selected), draft);
      await refresh();
      setSelected(result);
      setDraft(toDraft(result));
      setMode("preview");
      const context = await readObjectContext(authorizedFetch, "WORK_PACKAGE", result.id);
      setObjectActions(context.availableActions);
    });
  };

  const validate = async () => {
    const workPackage = required(selected);
    await run(async () => {
      const result = await validateWorkPackage(authorizedFetch, workPackage);
      setValidation(result);
      if (result.valid) {
        const updated = {
          ...workPackage,
          version: result.version,
          validatedVersion: result.version
        };
        setSelected(updated);
        setItems(current => current.map(item => item.id === updated.id ? updated : item));
        const context = await readObjectContext(authorizedFetch, "WORK_PACKAGE", updated.id);
        setObjectActions(context.availableActions);
      }
    });
  };

  const submit = async () => {
    await run(async () => {
      const result = await submitWorkPackage(authorizedFetch, required(selected));
      setSelected(result);
      setItems(current => current.map(item => item.id === result.id ? result : item));
      setValidation(null);
      const context = await readObjectContext(authorizedFetch, "WORK_PACKAGE", result.id);
      setObjectActions(context.availableActions);
    });
  };

  const showHistory = async (workPackage: WorkPackage) => {
    await run(async () => {
      setSelected(workPackage);
      setVersions(await listWorkPackageVersions(authorizedFetch, workPackage.id));
      setMode("history");
    });
  };

  const discard = async () => {
    if (!discardReason.trim()) {
      setError("A discard rationale is required.");
      return;
    }
    await run(async () => {
      const result = await discardWorkPackage(
        authorizedFetch, required(selected), discardReason
      );
      setItems(current => current.map(item => item.id === result.id ? result : item));
      setSelected(result);
      setMode("preview");
      setDiscardReason("");
      const context = await readObjectContext(authorizedFetch, "WORK_PACKAGE", result.id);
      setObjectActions(context.availableActions);
    });
  };

  const correctApprover = async () => {
    if (!correctedApprover || !correctionReason.trim()) {
      setError("Select a resolvable approver and provide a correction rationale.");
      return;
    }
    await run(async () => {
      const result = await reviseWorkPackageApprover(
        authorizedFetch, required(selected), correctedApprover,
        correctionReason.trim()
      );
      setItems(current => current.map(item => item.id === result.id ? result : item));
      setSelected(result);
      setDraft(toDraft(result));
      setCorrectionReason("");
      setCorrectedApprover("");
      setMode("preview");
      const context = await readObjectContext(authorizedFetch, "WORK_PACKAGE", result.id);
      setObjectActions(context.availableActions);
    });
  };

  const run = async (operation: () => Promise<void>) => {
    setBusy(true);
    setError("");
    try {
      await operation();
    } catch (problem) {
      setError(message(problem));
    } finally {
      setBusy(false);
    }
  };

  return <>
    <PageHeader eyebrow="Planning / Governed authoring" title="Work packages"
      description="Create and version scoped factory intent. Submission requires exact-version validation; activation remains with a different named human."
      aside={mode === "list" && mayAuthor
        ? <button className="primary-action" type="button" onClick={beginCreate}>Create work package</button>
        : undefined} />
    <section className="panel page-panel work-package-page">
      <div className="boundary-banner" role="note">
        <strong>Internal authoring only</strong>
        <span>GitLab writes, merge, release, deployment, and external mutations remain disabled.</span>
      </div>
      {error && <p className="form-message error" role="alert">{error}</p>}
      {mode === "list" && <PackageTable items={items}
        onPreview={item => void preview(item)} onHistory={showHistory} />}
      {(mode === "create" || mode === "edit") && <DraftForm
        draft={draft} setDraft={setDraft} editing={mode === "edit"}
        approvers={approvers} programs={programs} capabilities={capabilities}
        busy={busy} onSubmit={save} onCancel={close} />}
      {(mode === "preview" || mode === "discard" || mode === "correct-approver") && selected && <Preview
        workPackage={selected} validation={validation}
        mayEdit={available(objectActions, "UPDATE_WORK_PACKAGE")}
        mayValidate={available(objectActions, "VALIDATE_WORK_PACKAGE")}
        showSubmit={objectActions.some(action => action.actionId === "SUBMIT_WORK_PACKAGE")}
        maySubmit={available(objectActions, "SUBMIT_WORK_PACKAGE")}
        mayDiscard={available(objectActions, "DISCARD_WORK_PACKAGE")}
        mayCorrect={available(objectActions, "CORRECT_WORK_PACKAGE_APPROVER")}
        busy={busy} onBack={close} onEdit={() => beginEdit(selected)}
        onValidate={validate} onSubmit={submit}
        onHistory={() => void showHistory(selected)}
        onBeginDiscard={() => setMode("discard")}
        onBeginCorrection={() => setMode("correct-approver")} />}
      {mode === "discard" && selected && <section className="discard-panel" aria-labelledby="discard-heading">
        <h2 id="discard-heading">Discard Draft</h2>
        <p>The Draft will be closed; its immutable versions and audit events remain.</p>
        <label>Rationale<textarea value={discardReason}
          onChange={event => setDiscardReason(event.target.value)} /></label>
        <div className="form-actions"><button type="button" onClick={() => setMode("preview")}>Keep Draft</button>
          <button className="danger-action" type="button" disabled={busy} onClick={() => void discard()}>Discard Draft</button></div>
      </section>}
      {mode === "correct-approver" && selected && <section className="discard-panel" aria-labelledby="correction-heading">
        <h2 id="correction-heading">Correct named approver</h2>
        <p>Submitted v{selected.version} remains immutable. This governed correction supersedes its pending gate and creates a new Draft version requiring validation and resubmission.</p>
        <label>Resolvable named human<select required value={correctedApprover}
          onChange={event => setCorrectedApprover(event.target.value)}>
          <option value="">Select a Keycloak username</option>
          {approvers.filter(item => item.username !== selected.createdBy).map(item =>
            <option key={item.username} value={item.username}>{item.username} — {item.displayName}</option>)}
        </select></label>
        <label>Correction rationale<textarea value={correctionReason}
          onChange={event => setCorrectionReason(event.target.value)} /></label>
        <div className="form-actions"><button type="button" onClick={() => setMode("preview")}>Cancel</button>
          <button className="primary-action" type="button" disabled={busy || !correctedApprover || !correctionReason.trim()}
            onClick={() => void correctApprover()}>Create corrected Draft version</button></div>
      </section>}
      {mode === "history" && selected && <History workPackage={selected}
        versions={versions} onBack={() => void preview(selected)} />}
    </section>
  </>;
}

function PackageTable({items, onPreview, onHistory}: {
  items: WorkPackage[];
  onPreview(item: WorkPackage): void;
  onHistory(item: WorkPackage): void;
}) {
  if (items.length === 0) return <div className="empty-state"><h2>No work packages in this scope</h2><p>Create the first governed Draft for your company and business unit.</p></div>;
  return <div className="table-scroll" tabIndex={0} aria-label="Scrollable work package table"><table><thead><tr>
    <th>Work package</th><th>Owner organization</th><th>Status</th><th>Version</th><th><span className="visually-hidden">Actions</span></th>
  </tr></thead><tbody>{items.map(item => <tr key={item.id}><td><strong>{item.key}</strong><span>{item.title}</span></td>
    <td>{item.ownerOrganization}</td><td><Status value={item.status} /></td><td>v{item.version}</td>
    <td className="row-actions"><button type="button" onClick={() => onPreview(item)}>Preview</button>
      <button type="button" onClick={() => onHistory(item)}>Version history</button></td></tr>)}</tbody></table></div>;
}

function DraftForm({draft, setDraft, editing, approvers, programs, capabilities, busy, onSubmit, onCancel}: {
  draft: WorkPackageDraft;
  setDraft(value: WorkPackageDraft): void;
  editing: boolean;
  approvers: ApproverIdentity[];
  programs: Program[];
  capabilities: Capability[];
  busy: boolean;
  onSubmit(event: FormEvent): void;
  onCancel(): void;
}) {
  const text = (field: keyof WorkPackageDraft) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setDraft({...draft, [field]: event.target.value});
  const list = (field: keyof WorkPackageDraft) => (event: React.ChangeEvent<HTMLTextAreaElement>) =>
    setDraft({...draft, [field]: lines(event.target.value)});
  return <form className="authoring-form" onSubmit={onSubmit}>
    <div className="form-heading"><div><p className="eyebrow">{editing ? "Draft editor" : "New governed package"}</p>
      <h2>{editing ? `Edit ${draft.key}` : "Create work package"}</h2></div><span className="badge amber">Draft only</span></div>
    <div className="form-grid">
      <label>Key<input required pattern="[A-Z][A-Z0-9-]{2,39}" maxLength={40} value={draft.key} onChange={text("key")} aria-describedby="key-help" /><small id="key-help">Uppercase, stable, unique in scope.</small></label>
      <label>Title<input required maxLength={240} value={draft.title} onChange={text("title")} /></label>
      <label className="span-2">Objective<textarea required rows={4} maxLength={4000} value={draft.objective} onChange={text("objective")} /></label>
      <label>Program context<select value={draft.programId ?? ""}
        onChange={event => setDraft({...draft, programId: event.target.value || null, capabilityId: null})}>
        <option value="">Unassigned Draft — validation blocked</option>
        {programs.map(program => <option key={program.id} value={program.id}>{program.key} — {program.title}</option>)}
      </select><small>An ACTIVE same-scope Program is required before validation and submission.</small></label>
      <label>Capability context<select value={draft.capabilityId ?? ""}
        onChange={event => setDraft({...draft, capabilityId: event.target.value || null})}>
        <option value="">Unassigned Draft — validation blocked</option>
        {capabilities.filter(capability => programs.find(program => program.id === draft.programId)
          ?.productRelationships.some(relationship => relationship.productId === capability.productId))
          .map(capability => <option key={capability.id} value={capability.id}>{capability.key} — {capability.title}</option>)}
      </select><small>The AVAILABLE Capability must belong to a Product linked to the selected Program.</small></label>
      <label>Owner organization<input required value={draft.ownerOrganization} onChange={text("ownerOrganization")} /></label>
      <label>Named approver<select required value={draft.namedApprover}
        onChange={event => setDraft({...draft, namedApprover: event.target.value})}>
        <option value="">Select a Keycloak username</option>
        {draft.namedApprover && !approvers.some(item => item.username === draft.namedApprover)
          && <option value={draft.namedApprover} disabled>{draft.namedApprover} — unresolved</option>}
        {approvers.map(item => <option key={item.username} value={item.username}>{item.username} — {item.displayName}</option>)}
      </select><small>Same-scope human observed with workbench-approver.</small></label>
      <label>Risk<select value={draft.risk} onChange={event => setDraft({...draft, risk: event.target.value as WorkPackageDraft["risk"]})}><option>LOW</option><option>MEDIUM</option><option>HIGH</option></select></label>
      <label>Priority<select value={draft.priority} onChange={event => setDraft({...draft, priority: event.target.value as WorkPackageDraft["priority"]})}><option>LOW</option><option>MEDIUM</option><option>HIGH</option><option>CRITICAL</option></select></label>
      <ListField label="Supporting organizations" value={draft.supportingOrganizations} onChange={list("supportingOrganizations")} optional />
      <ListField label="Dependencies" value={draft.dependencies} onChange={list("dependencies")} optional />
      <ListField label="Deliverables" value={draft.deliverables} onChange={list("deliverables")} />
      <ListField label="Restrictions" value={draft.restrictions} onChange={list("restrictions")} />
      <ListField label="Acceptance gates" value={draft.acceptanceGates} onChange={list("acceptanceGates")} />
      <ListField label="Evidence requirements" value={draft.evidenceRequirements} onChange={list("evidenceRequirements")} />
    </div>
    <div className="form-actions"><button type="button" onClick={onCancel}>Cancel</button>
      <button className="primary-action" type="submit" disabled={busy}>{busy ? "Saving…" : editing ? "Save new version" : "Create Draft"}</button></div>
  </form>;
}

function ListField({label, value, onChange, optional = false}: {
  label: string;
  value: string[];
  onChange(event: React.ChangeEvent<HTMLTextAreaElement>): void;
  optional?: boolean;
}) {
  return <label>{label}<textarea required={!optional} rows={3} value={value.join("\n")} onChange={onChange} />
    <small>One item per line{optional ? " (optional)" : ""}.</small></label>;
}

function Preview({workPackage, validation, mayEdit, mayValidate, showSubmit, maySubmit, mayDiscard, mayCorrect, busy, onBack, onEdit, onValidate, onSubmit, onHistory, onBeginDiscard, onBeginCorrection}: {
  workPackage: WorkPackage;
  validation: WorkPackageValidation | null;
  mayEdit: boolean;
  mayValidate: boolean;
  showSubmit: boolean;
  maySubmit: boolean;
  mayDiscard: boolean;
  mayCorrect: boolean;
  busy: boolean;
  onBack(): void;
  onEdit(): void;
  onValidate(): void;
  onSubmit(): void;
  onHistory(): void;
  onBeginDiscard(): void;
  onBeginCorrection(): void;
}) {
  const validated = workPackage.validatedVersion === workPackage.version;
  return <article className="package-preview">
    <header><div><p className="eyebrow">Preview · version {workPackage.version}</p><h2>{workPackage.key} — {workPackage.title}</h2></div><Status value={workPackage.status} /></header>
    <p className="objective">{workPackage.objective}</p>
    <dl className="preview-meta"><div><dt>Scope</dt><dd>{workPackage.company} / {workPackage.businessUnit}</dd></div>
      <div><dt>Program</dt><dd>{workPackage.programKey ?? workPackage.programContextState.replaceAll("_", " ")}</dd></div>
      <div><dt>Product / Capability</dt><dd>{workPackage.productKey ?? "Unassigned"} / {workPackage.capabilityKey ?? "Unassigned"}</dd></div>
      <div><dt>Owner</dt><dd>{workPackage.ownerOrganization}</dd></div><div><dt>Risk / priority</dt><dd>{workPackage.risk} / {workPackage.priority}</dd></div>
      <div><dt>Named approver</dt><dd>{workPackage.namedApprover}</dd></div><div><dt>Created by</dt><dd>{workPackage.createdBy}</dd></div>
      <div><dt>Validation</dt><dd>{validated ? "Current version validated" : "Required before submission"}</dd></div></dl>
    <div className="preview-grid"><PreviewList title="Supporting organizations" items={workPackage.supportingOrganizations} />
      <PreviewList title="Dependencies" items={workPackage.dependencies} /><PreviewList title="Deliverables" items={workPackage.deliverables} />
      <PreviewList title="Restrictions" items={workPackage.restrictions} /><PreviewList title="Acceptance gates" items={workPackage.acceptanceGates} />
      <PreviewList title="Evidence requirements" items={workPackage.evidenceRequirements} /></div>
    {validation && <ValidationSummary summary={validation} />}
    <div className="form-actions wrap"><button type="button" onClick={onBack}>Back to work packages</button><button type="button" onClick={onHistory}>Version history</button>
      {workPackage.status === "SUBMITTED" && <a className="primary-action" href={`/approvals#approval-${workPackage.id}`}>Open approval task for v{workPackage.version}</a>}
      {mayEdit && <button type="button" onClick={onEdit}>Edit Draft</button>}
      {mayValidate && <button type="button" disabled={busy} onClick={onValidate}>Validate</button>}
      {showSubmit && <button className="primary-action" type="button" disabled={busy || !validated || !maySubmit} onClick={onSubmit}>Submit for approval</button>}
      {mayDiscard && <button className="danger-link" type="button" onClick={onBeginDiscard}>Discard Draft</button>}
      {mayCorrect && workPackage.status === "SUBMITTED" && <button type="button" onClick={onBeginCorrection}>Correct named approver</button>}</div>
  </article>;
}

function ValidationSummary({summary}: {summary: WorkPackageValidation}) {
  return <section className={`validation-summary ${summary.valid ? "valid" : "invalid"}`} aria-live="polite">
    <h3>{summary.valid ? "Validation passed" : "Validation requires attention"}</h3>
    {summary.errors.length > 0 && <ul>{summary.errors.map(issue => <li key={`${issue.field}-${issue.message}`}><strong>{issue.field}</strong>: {issue.message}</li>)}</ul>}
    {summary.warnings.length > 0 && <><h4>Warnings</h4><ul>{summary.warnings.map(issue => <li key={`${issue.field}-${issue.message}`}>{issue.message}</li>)}</ul></>}
  </section>;
}

function History({workPackage, versions, onBack}: {workPackage: WorkPackage; versions: WorkPackageVersion[]; onBack(): void}) {
  return <section className="version-history"><header><div><p className="eyebrow">Immutable record</p><h2>{workPackage.key} version history</h2></div><button type="button" onClick={onBack}>Back to preview</button></header>
    <ol>{versions.map(version => <li key={version.id}><span>v{version.version}</span><div><strong>{version.eventType}</strong><small>{new Date(version.recordedAt).toLocaleString()} · {version.actorId}</small></div><code>{version.correlationId.slice(0, 8)}</code></li>)}</ol></section>;
}

function PreviewList({title, items}: {title: string; items: string[]}) {
  return <section><h3>{title}</h3>{items.length > 0 ? <ul>{items.map(item => <li key={item}>{item}</li>)}</ul> : <p>None declared</p>}</section>;
}

function Status({value}: {value: string}) {
  const tone = value === "ACTIVE" ? "safe" : value === "SUBMITTED" ? "amber" : value === "DISCARDED" ? "deny" : "";
  return <span className={`badge ${tone}`}>{value.replaceAll("_", " ")}</span>;
}

function lines(value: string): string[] {
  return value.split("\n").map(item => item.trim()).filter(Boolean);
}

function toDraft(workPackage: WorkPackage): WorkPackageDraft {
  return {
    key: workPackage.key,
    title: workPackage.title,
    objective: workPackage.objective,
    programId: workPackage.programId,
    capabilityId: workPackage.capabilityId,
    ownerOrganization: workPackage.ownerOrganization,
    supportingOrganizations: workPackage.supportingOrganizations,
    risk: workPackage.risk,
    priority: workPackage.priority,
    dependencies: workPackage.dependencies,
    deliverables: workPackage.deliverables,
    restrictions: workPackage.restrictions,
    acceptanceGates: workPackage.acceptanceGates,
    namedApprover: workPackage.namedApprover,
    evidenceRequirements: workPackage.evidenceRequirements
  };
}

function required(value: WorkPackage | null): WorkPackage {
  if (!value) throw new Error("Select a work package first");
  return value;
}

function message(problem: unknown): string {
  return problem instanceof Error ? problem.message : "The authoring operation failed";
}

function available(actions: AvailableAction[], actionId: string): boolean {
  return actions.some(action => action.actionId === actionId && action.available);
}
